import React, { useState, useEffect } from "react";
import Modal from "components/modal";

const InscriptionFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialInscription, 
  isEditing,
  membres = [],
  anneesUniv = [],
  niveaux = [],
  parcours = [],
  mentions = [],
  loading = false
}) => {
  const [inscription, setInscription] = useState({
    id_membre: "",
    id_anneuniv: "",
    id_niveau: "",
    id_parcours: "",
    id_mention: "",
    date_inscrit: new Date().toISOString().split('T')[0]
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && initialInscription) {
      setInscription({
        id_membre: initialInscription.id_membre || "",
        id_anneuniv: initialInscription.id_anneuniv || "",
        id_niveau: initialInscription.id_niveau || "",
        id_parcours: initialInscription.id_parcours || "",
        id_mention: initialInscription.id_mention || "",
        date_inscrit: initialInscription.date_inscrit || new Date().toISOString().split('T')[0]
      });
      setValidationErrors({});
    }
  }, [isOpen, initialInscription]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInscription({ ...inscription, [name]: value });

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!inscription.id_membre) {
      errors.id_membre = "Le membre est requis";
    }

    if (!inscription.id_anneuniv) {
      errors.id_anneuniv = "L'année universitaire est requise";
    }

    if (!inscription.id_niveau) {
      errors.id_niveau = "Le niveau est requis";
    }

    if (!inscription.id_parcours) {
      errors.id_parcours = "Le parcours est requis";
    }

    if (!inscription.id_mention) {
      errors.id_mention = "La mention est requise";
    }

    if (!inscription.date_inscrit) {
      errors.date_inscrit = "La date d'inscription est requise";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    const result = await onSubmit({
      id_membre: inscription.id_membre,
      id_anneuniv: inscription.id_anneuniv,
      id_niveau: inscription.id_niveau,
      id_parcours: inscription.id_parcours,
      id_mention: inscription.id_mention,
      date_inscrit: inscription.date_inscrit,
    });
    
    if (!result.success) {
      setValidationErrors(result.errors || {});
    }
    
    setSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="md">
      <div className="text-center sm:text-left">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEditing
              ? "Modifier l'inscription"
              : "Réinscrire un étudiant"}
          </h3>
          <div className="h-1 w-12 bg-purple-600 mt-2 rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="id_membre"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Étudiant *
            </label>
            <select
              id="id_membre"
              name="id_membre"
              value={inscription.id_membre}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-purple-500"
            >
              <option value="">Sélectionner un étudiant</option>
              {membres.map(membre => (
                <option key={membre.id_membre} value={membre.id_membre}>
                  {membre.nom_prenom_membre} {membre.num_matricule ? `(${membre.num_matricule})` : ''}
                </option>
              ))}
            </select>
            {validationErrors.id_membre && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.id_membre}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="id_anneuniv"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Année universitaire *
              </label>
              <select
                id="id_anneuniv"
                name="id_anneuniv"
                value={inscription.id_anneuniv}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              >
                <option value="">Sélectionner une année</option>
                {anneesUniv.map(annee => (
                  <option key={annee.id_anneuniv} value={annee.id_anneuniv}>
                    {annee.dateuniv}
                  </option>
                ))}
              </select>
              {validationErrors.id_anneuniv && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.id_anneuniv}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="id_niveau"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Niveau *
              </label>
              <select
                id="id_niveau"
                name="id_niveau"
                value={inscription.id_niveau}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              >
                <option value="">Sélectionner un niveau</option>
                {niveaux.map(niveau => (
                  <option key={niveau.id_niveau} value={niveau.id_niveau}>
                    {niveau.name_niveau}
                  </option>
                ))}
              </select>
              {validationErrors.id_niveau && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.id_niveau}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="id_parcours"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Parcours *
              </label>
              <select
                id="id_parcours"
                name="id_parcours"
                value={inscription.id_parcours}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              >
                <option value="">Sélectionner un parcours</option>
                {parcours.map(p => (
                  <option key={p.id_parcours} value={p.id_parcours}>
                    {p.name_parcours}
                  </option>
                ))}
              </select>
              {validationErrors.id_parcours && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.id_parcours}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="id_mention"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mention *
              </label>
              <select
                id="id_mention"
                name="id_mention"
                value={inscription.id_mention}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              >
                <option value="">Sélectionner une mention</option>
                {mentions.map(mention => (
                  <option key={mention.id_mention} value={mention.id_mention}>
                    {mention.name_mention}
                  </option>
                ))}
              </select>
              {validationErrors.id_mention && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.id_mention}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="date_inscrit"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Date d'inscription *
            </label>
            <input
              type="date"
              name="date_inscrit"
              id="date_inscrit"
              value={inscription.date_inscrit}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-purple-500"
            />
            {validationErrors.date_inscrit && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.date_inscrit}
              </p>
            )}
          </div>

          {validationErrors.general && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-600">
                {validationErrors.general}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              onClick={onClose}
              disabled={submitting || loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm"
              disabled={submitting || loading}
            >
              {(submitting || loading) ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Chargement...
                </span>
              ) : isEditing ? (
                "Mettre à jour"
              ) : (
                "Réinscrire"
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InscriptionFormModal;